import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { HelpLink } from "@/features/docs/HelpLink";
import { FeedbackTabStrip } from "./FeedbackTabStrip";
import { LinkedPlayersStrip } from "./components/LinkedPlayersStrip";
import { FeedbackOverviewTab } from "./FeedbackOverviewTab";
import { FeedbackReviewTab } from "./FeedbackReviewTab";
import { FeedbackHistoryTab } from "./FeedbackHistoryTab";
import { FeedbackGoalsTab } from "./FeedbackGoalsTab";
import {
  FEEDBACK_TAB_LABELS,
  type FeedbackTab,
  normalizeFeedbackTab,
} from "./feedbackTabs";
import {
  reviewedClipIds as deriveReviewedClipIds,
  reviewedNoteIds as deriveReviewedNoteIds,
  reviewedPlaylistIds as deriveReviewedPlaylistIds,
  reviewedSummaryIds as deriveReviewedSummaryIds,
} from "./feedbackHelpers";
import { useMyFeedback } from "./hooks/useMyFeedback";
import { TeamScopeBar } from "@/features/navigation/TeamScopeBar";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

/**
 * Player/guardian feedback shell — the host for the `/feedback`
 * viewer surfaces.
 *
 * PR 1 of the Feedback Player UX redesign: the four action tabs
 * (Overview · Review · Goals · History) replace the legacy object
 * tabs. The router still accepts the five legacy `?tab=` values
 * (`playlists`, `notes`, `clips`, `summaries`, `development`) and the
 * shell folds them via `normalizeFeedbackTab`; no URL rewrite, so
 * back-button history stays stable.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * `/api/my-feedback` resolves the viewer's team from the request
 * scope, so the scope store must be populated first.
 *
 * `useMyFeedback()` is the single mount-time bundle fetch feeding
 * every tab. A 401 / missing token routes to `/login?next=...`.
 *
 * PR 2 ships the unified Review feed + filter chips; PR 3 reshapes
 * the Goals page (action-plan-first via `FeedbackGoalsTab`, the
 * viewer-mode wrapper around the shared development data); PR 5
 * upgrades the History wrapper into a grouped archive.
 */

export function FeedbackShell() {
  // Reconcile auth + scope — `/api/my-feedback` is scope-resolved
  // server-side, so a direct nav must not leave the scope store
  // unpopulated.
  useMe();

  const navigate = useNavigate({ from: "/feedback" });
  const search = useSearch({ from: "/feedback" });
  // Action-first default: bare `/feedback` ⇒ Overview. Legacy
  // `?tab=` values fold to the right action tab.
  const activeTab: FeedbackTab = normalizeFeedbackTab(search.tab);

  const authMe = useAuthStore((s) => s.me);
  const canOpenFeedback = getPublicNavCapabilities(authMe).canOpenFeedback;
  const signedInWithoutFeedbackAccess = !!authMe?.user && !canOpenFeedback;
  const feedback = useMyFeedback(!signedInWithoutFeedbackAccess);
  // Multi-team users must pick a team before the feedback tabs render.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const bundle = feedback.data;

  // The viewer's reviewed-id sets, derived once from `bundle.reviews`
  // — drive the Reviewed/New pills on the Notes + Playlists cards
  // AND the Overview's "what's new" filtering.
  const reviewedNoteIds = useMemo(
    () => deriveReviewedNoteIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  const reviewedPlaylistIds = useMemo(
    () => deriveReviewedPlaylistIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // PR 7: clips can carry review rows now. Threaded through to the
  // player-facing surfaces so clip cards and clip modals show the right
  // Mark done / Done ✓ affordance.
  const reviewedClipIds = useMemo(
    () => deriveReviewedClipIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // Phase B: match recaps are markable. Threaded to the Review +
  // History tabs so recap cards show Mark read / Read ✓ and age out
  // of the New band once read.
  const reviewedSummaryIds = useMemo(
    () => deriveReviewedSummaryIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // M9a A8 — auth gate. A 401 from /api/my-feedback redirects to
  // `/login?next=<current path>` (replaces the M4 <InlineLoginPanel>
  // that used to render inline). Kratos sessions are cookie-backed, so
  // the protected API response is the source of truth rather than the
  // legacy localStorage bearer token.
  const isAuthError =
    feedback.error instanceof ApiError && feedback.error.status === 401;
  const showLogin = isAuthError;

  // Guard against re-fire after the redirect changes the URL —
  // see AdminShell for the full explanation.
  //
  // `next` captures the FULL path + search (`/feedback?tab=goals`)
  // at mount via a ref so a viewer deep-linked to a specific tab
  // lands back on that tab after signing in.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/feedback"
      : "/feedback",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!signedInWithoutFeedbackAccess) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, signedInWithoutFeedbackAccess, navigate]);

  if (signedInWithoutFeedbackAccess) {
    return (
      <main className="min-h-screen p-4 text-ink sm:p-6" data-testid="feedback-shell">
        <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen p-4 text-ink sm:p-6" data-testid="feedback-shell">
      <header className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="min-w-0">
          <h1 className="flex items-center gap-1.5 text-2xl font-display tracking-tight sm:text-3xl">
            My Feedback
            <HelpLink />
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            Your coaching notes, clips, match recaps, and goals.
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <TeamScopeBar />
        </div>
      </header>

      {showLogin ? null : selectionRequired ? (
        <TeamScopeGate />
      ) : (
        <>
          {/* Linked-players chip strip — who's linked to this
           * account (empty ⇒ "ask a coach"). */}
          <div className="mb-4">
            <LinkedPlayersStrip
              players={feedback.data?.players ?? []}
              photoRequests={feedback.data?.player_photo_requests ?? []}
            />
          </div>

          {/* The sub-tab strip leads the tab content. */}
          <div className="mb-4">
            <FeedbackTabStrip active={activeTab} />
          </div>

          {/* Non-auth bundle errors surface here. */}
          {feedback.isError && !isAuthError ? (
            <p
              className="text-destructive font-mono text-sm mb-4"
              data-testid="feedback-bundle-error"
            >
              Could not load your feedback:{" "}
              {feedback.error instanceof Error
                ? feedback.error.message
                : "unknown error"}
            </p>
          ) : null}

          <section
            aria-label={FEEDBACK_TAB_LABELS[activeTab]}
            data-testid={`feedback-panel-${activeTab}`}
          >
            {/* Loading the bundle — show a light placeholder so the
             * panel isn't empty mid-fetch. */}
            {feedback.isLoading ? (
              <p
                className="text-ink-muted text-sm font-mono px-3 py-6"
                data-testid="feedback-loading"
              >
                Loading your feedback…
              </p>
            ) : activeTab === "overview" ? (
              <FeedbackOverviewTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : activeTab === "review" ? (
              <FeedbackReviewTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                /* PR 7: clip review state — drives the Mark done /
                 * Done ✓ affordance on clip cards. Empty for
                 * pre-PR 7 bundles, which keeps clips `new`. */
                reviewedClipIds={reviewedClipIds}
                /* Phase B: recap read state — drives Mark read /
                 * Read ✓ on summary cards. */
                reviewedSummaryIds={reviewedSummaryIds}
                /* PR 2: `?summary=<id>` deep-links to a specific
                 * recap. The Overview's "Read full recap →" /
                 * "Read summary" actions pass this so the Review
                 * tab auto-applies the Summaries filter + expands
                 * the matching card on mount. */
                initialSummaryId={search.summary ?? null}
              />
            ) : activeTab === "goals" ? (
              <FeedbackGoalsTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : (
              <FeedbackHistoryTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
                reviewedSummaryIds={reviewedSummaryIds}
              />
            )}
          </section>
        </>
      )}
    </main>
  );
}
